import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../../components/core/LoadingSpinner";
import {
  BookingActionContext,
  BookingActionService,
  CancelData,
  CancellationRefundOutcome,
} from "../../../services/bookingActionService";
import { MeetingTypeActionHandler } from "../../../services/meetingTypeActionHandler";
import BookingDetailsCard from "./BookingDetailsCard";

const ZERO_DECIMAL_CCY = new Set(['BIF','CLP','DJF','GNF','JPY','KMF','KRW','MGA','PYG','RWF','UGX','VND','VUV','XAF','XOF','XPF']);
function formatRefund(amount: number | undefined, currency: string | undefined): string {
  if (amount === undefined || amount === null) return '';
  const cur = (currency || 'USD').toUpperCase();
  const minDigits = ZERO_DECIMAL_CCY.has(cur) ? 0 : 2;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, minimumFractionDigits: minDigits, maximumFractionDigits: minDigits }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
}

interface CancelFlowProps {
  bookingContext: BookingActionContext;
  token: string;
}

const CancelFlow: React.FC<CancelFlowProps> = ({ bookingContext, token }) => {
  const { t } = useTranslation('booking');
  const [cancelReason, setCancelReason] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [refundOutcome, setRefundOutcome] = useState<CancellationRefundOutcome | null>(null);
  
  // Validate cancel action
  const validation = MeetingTypeActionHandler.validateAction(bookingContext, 'cancel');

  const handleCancel = async () => {
    setLoading(true);
    setError("");

    try {
      const cancelData: CancelData = {
        cancelReason: cancelReason.trim() || undefined,
      };

      const result = await BookingActionService.submitCancellation(token, cancelData);
      setRefundOutcome(result.refund);
      setSuccess(true);
    } catch (error: any) {
      setError(error.message || "Failed to cancel meeting");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {t('cancel.success.title')}
            </h2>
            <p className="text-gray-600">
              {t('cancel.success.message')}
            </p>
          </div>

          {/* Phase 5 — Refund outcome panel */}
          {refundOutcome && refundOutcome.status !== 'skipped' && (
            <RefundOutcomePanel
              outcome={refundOutcome}
              currency={(bookingContext as any).currency}
            />
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">{t('cancel.success.meeting')}</p>
                <p className="text-sm font-medium text-gray-900">
                  {bookingContext.title || bookingContext.meetingType.title}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">{t('cancel.success.originalTime')}</p>
                <p className="text-sm font-medium text-gray-900">
                  {bookingContext.startTime.toLocaleString([], {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}
                </p>
              </div>

              {cancelReason && (
                <div>
                  <p className="text-sm text-gray-500">{t('cancel.success.cancelReason')}</p>
                  <p className="text-sm text-gray-900 italic">"{cancelReason}"</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 lg:px-6 lg:pt-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {t('cancel.title')}
          </h1>
        </div>

        {/* Meeting Details */}
        <div className="mb-6">
          <BookingDetailsCard booking={bookingContext} />
        </div>

        {/* Validation Check */}
        {!validation.isValid ? (
          <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-6">
            <p className="text-sm sm:text-base">{validation.message}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            {/* Cancellation Reason */}
            <div className="mb-6">
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                {t('cancel.reason.label')}
              </label>
              <textarea
                id="reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                placeholder={t('cancel.reason.placeholder')}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                {t('cancel.reason.helpText')}
              </p>
            </div>

            {/* Impact Warning */}
            {bookingContext.meetingType.type === 'event' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-medium text-amber-800 mb-1">{t('cancel.warnings.eventTitle')}</h4>
                    <p className="text-sm text-amber-700">
                      {t('cancel.warnings.eventMessage')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Action Button */}
            <div className="flex justify-center">
              <button
                onClick={handleCancel}
                disabled={loading}
                className={`w-full sm:w-auto px-6 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed focus:ring-gray-500'
                    : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner size="sm" color="white" className="mr-2" />
                    {t('cancel.canceling')}
                  </span>
                ) : (
                  t('cancel.cancelMeeting')
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Phase 5 — Refund outcome panel shown on the cancellation success screen.
 * Surfaces what happened to the invitee's money in plain language.
 */
const RefundOutcomePanel: React.FC<{
  outcome: CancellationRefundOutcome;
  currency?: string;
}> = ({ outcome, currency }) => {
  const { t } = useTranslation('payments');

  if (outcome.status === 'refunded' || outcome.status === 'partially_refunded') {
    const amount = formatRefund(outcome.amountRefunded, currency);
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-green-700 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div className="text-sm">
            <p className="font-semibold text-green-900">
              {t('refund.outcome.refunded.title', 'Refund issued')}
            </p>
            <p className="text-green-800 mt-1">
              {t(
                'refund.outcome.refunded.body',
                'You will receive {{amount}} back to your original payment method within 5–10 business days.',
                { amount }
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (outcome.status === 'not_eligible') {
    const reasonKey = `refund.outcome.notEligible.reasons.${outcome.reason || 'default'}`;
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4">
        <p className="text-sm font-semibold text-gray-900 mb-1">
          {t('refund.outcome.notEligible.title', 'No refund issued')}
        </p>
        <p className="text-sm text-gray-700">
          {t(reasonKey, t('refund.outcome.notEligible.default', 'This booking is not eligible for an automatic refund per the host\'s policy. Contact the host directly if you have questions.'))}
        </p>
      </div>
    );
  }

  if (outcome.status === 'failed') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
        <p className="text-sm font-semibold text-red-900 mb-1">
          {t('refund.outcome.failed.title', 'Refund could not be issued')}
        </p>
        <p className="text-sm text-red-800">
          {t(
            'refund.outcome.failed.body',
            'We were unable to process the refund automatically. Please contact the host directly.'
          )}
        </p>
      </div>
    );
  }

  return null;
};

export default CancelFlow;